import { computeMetrics, type SummaryMetrics } from '../../hooks/useSummaryPerStrokeData';
import type { PerformanceMetricsData } from '../chartDataFetchers';
import { buildOrderedStrokeTimestamps, isStrokeIndexAllowed } from './strokeAlignment';

export type StrokePowerPoint = { timestamp: number; power: number };

export type StrokeStabilityRow = {
  stroke_timestamp: number;
  clean_for_analysis: boolean;
  stability_index?: number | null;
  instability_score?: number | null;
};

export type HullStabilityComparison = {
  setTimestamps: Set<number>;
  setCount: number;
  notSetCount: number;
  totalScored: number;
  allMetrics: SummaryMetrics;
  setMetrics: SummaryMetrics;
};

/** Build per-stroke crew-average power from telemetry_stroke_metrics rows. */
export function buildStrokePowerSeries(
  strokeMetricRows: Record<string, unknown>[]
): StrokePowerPoint[] {
  const out: StrokePowerPoint[] = [];
  for (const row of strokeMetricRows) {
    const timestamp = Number(row.timestamp);
    if (!Number.isFinite(timestamp) || timestamp <= 0) continue;
    const powers: number[] = [];
    for (let seat = 1; seat <= 8; seat += 1) {
      const value = Number(row[`rower_${seat}_power`]);
      if (Number.isFinite(value) && value > 0) powers.push(value);
    }
    if (powers.length === 0) continue;
    out.push({
      timestamp,
      power: powers.reduce((sum, value) => sum + value, 0) / powers.length,
    });
  }
  return out;
}

/** Compare all stroke powers vs set-only (clean_for_analysis) strokes. */
function filterStrokeMetricRows(
  strokeMetricRows: Record<string, unknown>[],
  allowedTimestamps: Set<number> | null,
  allowedStrokeIndices: Set<number> | null = null
): Record<string, unknown>[] {
  if (!allowedTimestamps && !allowedStrokeIndices) return strokeMetricRows;

  let strokeIndex = 0;
  return strokeMetricRows.filter((row) => {
    const timestamp = Number(row.timestamp);
    if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || timestamp < 0) {
      return false;
    }
    strokeIndex += 1;
    if (allowedStrokeIndices) {
      return isStrokeIndexAllowed(strokeIndex, allowedStrokeIndices);
    }
    return allowedTimestamps!.has(timestamp);
  });
}

/** Re-aggregate IRP / performance-metrics data from filtered 0x800A stroke rows. */
export function aggregatePerformanceMetricsFromStrokeRows(
  strokeMetricRows: Record<string, unknown>[],
  allowedTimestamps: Set<number> | null = null,
  allowedStrokeIndices: Set<number> | null = null
): PerformanceMetricsData | null {
  const rows = filterStrokeMetricRows(strokeMetricRows, allowedTimestamps, allowedStrokeIndices);
  if (rows.length === 0) return null;

  const rowerPowers: Record<number, number[]> = {};
  const allPowers: number[] = [];
  let sessionStartTime = Infinity;
  let sessionEndTime = -Infinity;

  for (const metric of rows) {
    const timestamp = Number(metric.timestamp);
    if (!Number.isFinite(timestamp) || timestamp <= 0) continue;

    sessionStartTime = Math.min(sessionStartTime, timestamp);
    sessionEndTime = Math.max(sessionEndTime, timestamp);

    for (let rower = 1; rower <= 8; rower += 1) {
      const powerValue = Number(metric[`rower_${rower}_power`]);
      if (!Number.isFinite(powerValue) || powerValue <= 0) continue;
      if (!rowerPowers[rower]) rowerPowers[rower] = [];
      rowerPowers[rower].push(powerValue);
      allPowers.push(powerValue);
    }
  }

  if (allPowers.length === 0) return null;

  const averagePowerByRower: Record<number, number> = {};
  let totalPowerSum = 0;
  let rowerCount = 0;

  Object.entries(rowerPowers).forEach(([rower, powers]) => {
    if (powers.length === 0) return;
    const avg = powers.reduce((sum, value) => sum + value, 0) / powers.length;
    averagePowerByRower[Number(rower)] = Math.round(avg);
    totalPowerSum += avg;
    rowerCount += 1;
  });

  const overallAverage = rowerCount > 0 ? Math.round(totalPowerSum / rowerCount) : 0;
  const powerRange = { min: Math.min(...allPowers), max: Math.max(...allPowers) };
  const peakPower = powerRange.max;
  const mean = allPowers.reduce((sum, value) => sum + value, 0) / allPowers.length;
  const variance =
    allPowers.reduce((sum, value) => sum + (value - mean) ** 2, 0) / allPowers.length;
  const consistency = mean > 0 ? Math.round(100 - (Math.sqrt(variance) / mean) * 100) : 0;

  const enhancedRowerData = Object.entries(rowerPowers).map(([rower, powers]) => {
    const sortedPowers = [...powers].sort((a, b) => a - b);
    const p25Index = Math.floor(sortedPowers.length * 0.25);
    const p75Index = Math.floor(sortedPowers.length * 0.75);
    const rowerMean = powers.reduce((sum, value) => sum + value, 0) / powers.length;
    const rowerVariance =
      powers.reduce((sum, value) => sum + (value - rowerMean) ** 2, 0) / powers.length;
    const rowerStdDev = Math.sqrt(rowerVariance);
    const rowerConsistency = rowerMean > 0 ? Math.round(100 - (rowerStdDev / rowerMean) * 100) : 0;

    const minPower = Math.min(...powers);
    const maxPower = Math.max(...powers);
    const range = maxPower - minPower;
    const binCount = Math.min(20, Math.max(10, Math.floor(powers.length / 3)));
    const binWidth = range / binCount;
    const powerDistribution: Array<{
      start: number;
      end: number;
      count: number;
      frequency: number;
    }> = [];

    for (let i = 0; i < binCount; i += 1) {
      const start = minPower + i * binWidth;
      const end = minPower + (i + 1) * binWidth;
      const count = powers.filter(
        (value) => value >= start && value < (i === binCount - 1 ? end + 1 : end)
      ).length;
      if (count > 0) {
        powerDistribution.push({
          start: Math.round(start),
          end: Math.round(end),
          count,
          frequency: count / powers.length,
        });
      }
    }

    return {
      id: Number(rower),
      power: Math.round(rowerMean),
      min: minPower,
      max: maxPower,
      p25: sortedPowers[p25Index] || 0,
      p75: sortedPowers[p75Index] || 0,
      peak: Math.max(...powers),
      consistency: rowerConsistency,
      powerDistribution,
    };
  });

  const sessionDuration =
    sessionStartTime !== Infinity && sessionEndTime !== -Infinity
      ? (sessionEndTime - sessionStartTime) / (1000 * 60)
      : 0;

  return {
    averagePowerByRower,
    overallAverage,
    crewAverage: overallAverage,
    sessionDuration,
    dataPointCount: rows.length,
    rowerPowers: {},
    strokeCount: buildOrderedStrokeTimestamps(rows).length,
    individualStrokes: {},
    powerRange,
    peakPower,
    consistency,
    enhancedRowerData,
  };
}

export function compareHullStability(
  strokeSeries: StrokePowerPoint[],
  stabilityRows: StrokeStabilityRow[]
): HullStabilityComparison {
  const setTimestamps = new Set(
    stabilityRows.filter((row) => row.clean_for_analysis).map((row) => row.stroke_timestamp)
  );
  const setPowers = strokeSeries
    .filter((point) => setTimestamps.has(point.timestamp))
    .map((point) => point.power);
  const allPowers = strokeSeries.map((point) => point.power);
  const setCount = stabilityRows.filter((row) => row.clean_for_analysis).length;

  return {
    setTimestamps,
    setCount,
    notSetCount: Math.max(0, stabilityRows.length - setCount),
    totalScored: stabilityRows.length,
    allMetrics: computeMetrics(allPowers),
    setMetrics: computeMetrics(setPowers),
  };
}
